"use client";

import Translations from "@translate/components/EmailLogin/steps/EmailInputStepDesktop";
import React from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { EmailInputStepProps } from "../types";
import { SIGN_IN_PAGE_COPY } from "../signInPageCopy";
import { LoadingButton } from "../LoadingButton";
import {
  EmailInputField,
  useEmailInputCanSubmit,
} from "../shared/EmailInputField";
import { EmailLoginOrDivider } from "../shared/EmailLoginOrDivider";
import { SocialLoginButtons } from "../shared/SocialLoginButtons";
import { EmailLoginTermsCheckbox } from "../shared/EmailLoginTermsCheckbox";
import { SignInOrContinueLabel } from "../shared/SignInOrContinueLabel";

const I18n = I18nUtil.createScoped(Translations);

export function EmailInputStepDesktop({
  email,
  onEmailChange,
  onContinue,
  sending = false,
  emailFormatError = false,
  agreed,
  onAgreedChange,
  termsLink,
  privacyLink,
  onSocialClick,
  socialLoading = false,
  className,
  hideTitle = false,
  useV301SignInChrome = false,
}: EmailInputStepProps) {
  const canSubmit = useEmailInputCanSubmit(email, agreed, sending);
  const isV301Flat = hideTitle && useV301SignInChrome;
  const continueLabel = isV301Flat
    ? SIGN_IN_PAGE_COPY.continueWithEmail
    : I18n.t("Continue with email");

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        isV301Flat ? "gap-8" : "h-full justify-between",
        className,
      )}
      data-testid="email-input-step-desktop"
    >
      <div
        className={cn(
          "flex w-full flex-col",
          isV301Flat
            ? "gap-8"
            : "gap-10 md:items-center md:justify-center md:gap-8",
        )}
      >
        {!isV301Flat && (
          <div className="flex w-full flex-col items-start gap-3 md:mx-auto md:items-center md:gap-4">
            <h2
              aria-label="Email Input Step h2"
              data-openreplay-label="login_emailinputstep_h2"
              id="sem-email-input-step-h2-2"
              className="headline_regular_l text-content_primary md:title_xl md:text-center"
            >
              {I18n.t("Sign in / Sign up")}
            </h2>
          </div>
        )}

        <div
          className="flex w-full flex-col gap-6 md:gap-4"
          data-testid="sign-in-email-cta-group"
        >
          <EmailInputField
            variant="desktop"
            email={email}
            onEmailChange={onEmailChange}
            emailFormatError={emailFormatError}
            canSubmit={canSubmit}
            placeholder={
              isV301Flat ? SIGN_IN_PAGE_COPY.emailPlaceholder : undefined
            }
          />
          <LoadingButton
            data-openreplay-label="login_emailloginemailinputstep_click"
            type="button"
            disabled={!canSubmit}
            loading={sending}
            onClick={onContinue}
            className={cn(
              "button_l flex h-12 w-full items-center justify-center rounded-lg transition-opacity",
              canSubmit
                ? "bg-background_white_overlay100 text-content_inverse_primary"
                : "cursor-not-allowed bg-background_white_overlay20 text-content_inverse_primary",
            )}
          >
            {continueLabel}
          </LoadingButton>
        </div>
      </div>

      <div className="flex flex-col">
        {onSocialClick && (
          <div
            className={cn("flex flex-col", isV301Flat ? "gap-4" : "mb-4 gap-6")}
          >
            {isV301Flat ? (
              <SignInOrContinueLabel />
            ) : (
              <EmailLoginOrDivider variant="desktop" />
            )}
            <SocialLoginButtons
              variant="desktop"
              onSocialClick={onSocialClick}
              socialLoading={socialLoading}
            />
          </div>
        )}
        <EmailLoginTermsCheckbox
          variant="desktop"
          agreed={agreed}
          onAgreedChange={onAgreedChange}
          termsLink={termsLink}
          privacyLink={privacyLink}
          className={cn(isV301Flat ? "mt-4" : "mt-4 pb-10")}
        />
      </div>
    </div>
  );
}
